import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { supabase } from '../../lib/supabase';

export default function CustomersList() {
     const [customers, setCustomers] = useState([]);
     const [search, setSearch] = useState('');
     const [loading, setLoading] = useState(true);

     useEffect(() => {
          fetchCustomers();
     }, []);

     async function fetchCustomers() {
          // Get all customers with their booking counts and totals
          const { data: customerData } = await supabase
               .from('customers')
               .select('*')
               .order('created_at', { ascending: false });

          if (!customerData) {
               setLoading(false);
               return;
          }

          // Get booking stats per customer
          const { data: bookings } = await supabase
               .from('bookings')
               .select('customer_id, total_cents, status')
               .not('customer_id', 'is', null);

          const stats = {};
          if (bookings) {
               bookings.forEach((b) => {
                    if (!stats[b.customer_id]) {
                         stats[b.customer_id] = { count: 0, revenue: 0 };
                    }
                    stats[b.customer_id].count++;
                    if (b.status === 'confirmed' || b.status === 'completed') {
                         stats[b.customer_id].revenue += b.total_cents || 0;
                    }
               });
          }

          const enriched = customerData.map((c) => ({
               ...c,
               bookingCount: stats[c.id]?.count || 0,
               totalRevenue: stats[c.id]?.revenue || 0,
          }));

          setCustomers(enriched);
          setLoading(false);
     }

     const filtered = customers.filter((c) => {
          if (!search) return true;
          const q = search.toLowerCase();
          return (
               c.name?.toLowerCase().includes(q) ||
               c.email?.toLowerCase().includes(q) ||
               c.phone?.includes(q)
          );
     });

     const inputClasses =
          'bg-white border border-gray-200 rounded-lg px-4 py-2 text-darker placeholder-darker/30 focus:outline-none focus:border-yellow duration-300';

     if (loading) {
          return <p className='text-darker/60'>Loading customers...</p>;
     }

     return (
          <div>
               <div className='flex flex-col tablet:flex-row items-start tablet:items-center justify-between mb-6 gap-4'>
                    <h1 className='text-darker text-2xl font-bold'>Customers</h1>
                    <input
                         type='text'
                         value={search}
                         onChange={(e) => setSearch(e.target.value)}
                         placeholder='Search by name, email, or phone...'
                         className={inputClasses + ' w-full tablet:w-72'}
                    />
               </div>

               <div className='bg-white rounded-xl border border-gray-200 overflow-hidden'>
                    {filtered.length === 0 ? (
                         <p className='text-darker/40 p-4'>
                              {customers.length === 0
                                   ? 'No customers yet. Customers are created automatically from bookings.'
                                   : 'No customers match your search.'}
                         </p>
                    ) : (
                         <div className='overflow-x-auto'>
                              <table className='w-full text-left'>
                                   <thead>
                                        <tr className='border-b border-gray-200 text-darker/40 text-sm'>
                                             <th className='p-4'>Customer</th>
                                             <th className='p-4'>Phone</th>
                                             <th className='p-4'>Bookings</th>
                                             <th className='p-4'>Total Spent</th>
                                             <th className='p-4'>Since</th>
                                             <th className='p-4'></th>
                                        </tr>
                                   </thead>
                                   <tbody className='divide-y divide-gray-200'>
                                        {filtered.map((customer) => (
                                             <tr
                                                  key={customer.id}
                                                  className='hover:bg-gray-100 duration-300'
                                             >
                                                  <td className='p-4'>
                                                       <p className='text-darker'>
                                                            {customer.name}
                                                       </p>
                                                       <p className='text-darker/40 text-sm'>
                                                            {customer.email}
                                                       </p>
                                                  </td>
                                                  <td className='p-4 text-darker/60 text-sm'>
                                                       {customer.phone || '-'}
                                                  </td>
                                                  <td className='p-4 text-darker text-sm'>
                                                       {customer.bookingCount}
                                                  </td>
                                                  <td className='p-4 text-yellow font-bold text-sm'>
                                                       ${(customer.totalRevenue / 100).toLocaleString()}
                                                  </td>
                                                  <td className='p-4 text-darker/40 text-sm whitespace-nowrap'>
                                                       {format(new Date(customer.created_at), 'MMM d, yyyy')}
                                                  </td>
                                                  <td className='p-4'>
                                                       <Link
                                                            to={`/admin/customers/${customer.id}`}
                                                            className='text-yellow text-sm hover:underline'
                                                       >
                                                            View →
                                                       </Link>
                                                  </td>
                                             </tr>
                                        ))}
                                   </tbody>
                              </table>
                         </div>
                    )}
               </div>
          </div>
     );
}
